/**
 * Error boundary for BI Command Center sub-panels.
 *
 * Wraps each TabsContent body in BICommandCenterPage so a crash in one panel
 * doesn't black out the whole page. Retry button re-mounts the panel.
 *
 * Phase 4.8-B null-safety sweep — WQ-218
 */
import React from 'react';

interface Props {
  panelName: string;
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  resetKey: number;
}

export class BiPanelErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, resetKey: 0 };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error(`[BiPanel:${this.props.panelName}] crashed:`, error, info);
  }

  handleRetry = (): void => {
    this.setState(prev => ({ hasError: false, error: null, resetKey: prev.resetKey + 1 }));
  };

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <div className="glass" style={{ padding: 24, textAlign: 'center' }}>
          <p style={{ color: '#ef4444', marginBottom: 8, fontSize: 14, fontWeight: 600 }}>
            {this.props.panelName} failed to render
          </p>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, marginBottom: 16 }}>
            {this.state.error?.message || 'An unexpected error occurred.'}
          </p>
          <button
            onClick={this.handleRetry}
            className="glass"
            style={{
              padding: '6px 16px',
              cursor: 'pointer',
              color: '#00d4ff',
              background: 'rgba(0,212,255,0.1)',
              border: '1px solid rgba(0,212,255,0.3)',
              borderRadius: 6,
              fontSize: 13,
            }}
          >
            Retry
          </button>
        </div>
      );
    }
    return <React.Fragment key={this.state.resetKey}>{this.props.children}</React.Fragment>;
  }
}

export default BiPanelErrorBoundary;
